import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Sparkles,
  Star,
  Footprints,
  MessageCircle,
  Newspaper,
  Globe,
  Heart,
  UtensilsCrossed,
  Users,
} from "lucide-react";
import { Alignment, Fit, Layout, useRive } from "@rive-app/react-canvas";
import radarAnimation from "../../assets/rive/radar.riv";
import rainbowCloudAnimation from "../../assets/rive/rainbow_cloud.riv";
import martiniAnimation from "../../assets/rive/martini.riv";
import { Expandable } from "@/components/ux/Expandable";
import { Chip } from "@/components/ux/Chip";
import { ThemeToggle } from "@/components/ux/ThemeToggle";
import imgInstagram from "../../assets/icons/instagram.svg";
import imgTiktok from "../../assets/icons/tiktok.svg";
import { SectionBackground } from "@/components/ux/SectionBackground";
import logo from "../../assets/icons/outclose.svg";
import { HowItWorks } from "@/components/HowItWorks";
import Logo from "@/components/Logo";
import { Link } from "wouter";
import { LanguageSelector, useT } from "@/lib/i18n";
import NextDinner from "../../components/NextDinner";
import danielImg from "../../assets/images/daniel.webp";
import jayImg from "../../assets/images/jay.webp";
import davidImg from "../../assets/images/David.webp";
import menPeopleImg from "../../assets/images/people men 20s-30s.webp";
import timeoutImg from "../../assets/images/timeout.svg";
import themImg from "../../assets/images/them.svg";
import tracking from "../../lib/tracking";
import AppStoreBadges from "../../components/AppStoreBadges";

const QUIZ_HREF = "https://app.outclose.com/quiz?a=m";

const DIFFERENTIATORS = [
  { icon: Globe, key: "community" },
  { icon: Heart, key: "owned" },
  { icon: Sparkles, key: "tech" },
  { icon: UtensilsCrossed, key: "ops" },
] as const;

/**
 * Conquest landing page — a full-page variation of the /r3vk page
 * (LandingMen2040), tuned for Google Ads traffic coming from a competitor.
 * Leads with our differentiators (largest global community, gay owned &
 * operated, advanced app + matching, real-world operations) via a dedicated
 * "Why Outclose" strip, then reuses the existing social proof.
 */
export function LandingConquest() {
  const { t } = useT("landing");

  useEffect(() => {
    tracking.trackPage("Landing Page - Conquest");
  }, []);

  const { RiveComponent: RadarRive } = useRive({
    src: radarAnimation,
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover, alignment: Alignment.Center }),
  });

  const { RiveComponent: RainbowRive } = useRive({
    src: rainbowCloudAnimation,
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover, alignment: Alignment.Center }),
  });

  const { RiveComponent: MartiniRive } = useRive({
    src: martiniAnimation,
    autoplay: true,
    layout: new Layout({ fit: Fit.Cover, alignment: Alignment.TopCenter }),
  });

  const scrollToWhy = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    document.getElementById("why")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="relative overflow-hidden">
      <div className="z-10">
        <div className="max-w-7xl mx-auto px-6 flex items-center my-4 justify-between">
          <Logo link="/app" />
          <div className="flex items-center gap-6">
            <nav className="hidden md:flex items-center gap-6">
              <Link
                href="/affiliate"
                className="text-type-950 dark:text-neutral-100 hover:text-primary-500 transition-colors font-medium"
              >
                {t("nav.affiliate")}
              </Link>
              <Link
                href="/locations"
                className="text-type-950 dark:text-neutral-100 hover:text-primary-500 transition-colors font-medium"
              >
                {t("nav.locations")}
              </Link>
            </nav>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <a href="https://app.outclose.com/" className="btn-primary px-4 py-2 w-auto">
                {t("app:common.buttons.login")}
              </a>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center flex flex-col items-center justify-center"
          >
            <div className="min-h-screen flex flex-col justify-center items-center">
              <div className="glass-bar mb-5 mt-16">
                {t("pages.conquest.hero.tagline")}
              </div>

              <h1 className="text-4xl md:text-6xl font-semibold mb-4 text-type-950 dark:text-neutral-50 font-Poppins">
                {t("pages.conquest.hero.titleLine1")}
                <br />
                {t("pages.conquest.hero.titleLine2")}
              </h1>

              <p className="text-xl font-Poppins mb-4 max-w-2xl mx-auto dark:text-neutral-100">
                {t("pages.conquest.hero.subtitleLine1")}
                <br />
                {t("pages.conquest.hero.subtitleLine2")}
              </p>

              <p className="text-sm font-medium mb-6 flex items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black/60 backdrop-blur-md text-white border border-white/10">
                  {t("pages.conquest.hero.tablesGuys")}
                </span>
                <span className="text-white font-semibold">{t("pages.conquest.hero.tablesOr")}</span>
                <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-black/60 backdrop-blur-md text-white border border-white/10">
                  {t("pages.conquest.hero.tablesMixed")}
                </span>
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <a href="#why" onClick={scrollToWhy} className="w-full sm:w-auto">
                  <button className="btn glass-btn-secondary px-8 py-2 text-lg w-full">
                    {t("app:common.buttons.learnMore")}
                  </button>
                </a>
                <a
                  className="btn glass-btn-primary px-8 py-2 text-lg w-full sm:w-auto"
                  href={QUIZ_HREF}
                  target="_self"
                >
                  {t("cta.meetYourPeople")}
                  <Sparkles className="inline-block ml-2 w-5 h-5" />
                </a>
              </div>

              <NextDinner avatarImage={menPeopleImg} avatarClassName="h-[86px] w-full mx-auto my-4" />

              <AppStoreBadges className="justify-center" badgeHeight={40} source="landing_conquest_hero" />
              <div className="md:flex-[0_1_200px] flex-[0_1_100px] min-h-16" />
            </div>
          </motion.div>
        </div>

        {/* Why Outclose — differentiators */}
        <div id="why">
        <SectionBackground className="text-center py-16 justify-center items-center">
          <Chip
            title={t("pages.conquest.diff.chipTitle")}
            variant="primary"
            icon={<Sparkles strokeWidth={2} />}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto mt-8 px-4">
            {DIFFERENTIATORS.map(({ icon: Icon, key }) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="card p-6 text-left flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-type-950 dark:text-neutral-100">
                  {t(`pages.conquest.diff.${key}.title`)}
                </h3>
                <p className="text-sm text-dark-muted dark:text-neutral-400">
                  {t(`pages.conquest.diff.${key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>
        </SectionBackground>
        </div>

        {/* Every age */}
        <div className="bg-white dark:bg-neutral-900 text-center px-4 sm:px-6 py-16 lg:px-8">
          <Chip
            title={t("pages.conquest.age.chipTitle")}
            variant="primary"
            icon={<Users strokeWidth={2} />}
          />
          <h2 className="text-3xl md:text-4xl font-semibold mt-8 mb-4 text-type-950 dark:text-neutral-50 font-Poppins max-w-3xl mx-auto">
            {t("pages.conquest.age.title")}
          </h2>
          <p className="text-lg text-dark-muted dark:text-neutral-400 max-w-2xl mx-auto">
            {t("pages.conquest.age.description")}
          </p>
          <div className="flex flex-wrap justify-center gap-2 mt-8">
            {["20s", "30s", "40s", "50s", "60s", "70s"].map((r) => (
              <span
                key={r}
                className="px-4 py-2 rounded-full border border-primary-500/30 bg-primary-500/5 text-sm font-semibold text-type-950 dark:text-neutral-100"
              >
                {r}
              </span>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex justify-center items-center p-4">
            <Chip
              title={t("features.chipTitle")}
              variant="primary"
              icon={<Star strokeWidth={2} />}
            />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="card text-center"
            >
              <div className="w-full h-[200px] rounded-xl overflow-hidden">
                <RadarRive className="w-full h-full fit-cover" />
              </div>
              <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                {t("pages.conquest.features.matching.title")}
              </h3>
              <p className="text-dark-muted dark:text-neutral-400">
                {t("pages.conquest.features.matching.description")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="card text-center"
            >
              <div className="w-full h-[200px] rounded-xl overflow-hidden">
                <RainbowRive />
              </div>
              <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                {t("pages.conquest.features.app.title")}
              </h3>
              <p className="text-dark-muted dark:text-neutral-400">
                {t("pages.conquest.features.app.description")}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="card text-center"
            >
              <div className="w-full h-[200px] rounded-xl overflow-hidden">
                <MartiniRive />
              </div>
              <h3 className="text-xl font-semibold my-4 dark:text-neutral-100">
                {t("pages.conquest.features.noHassle.title")}
              </h3>
              <p className="text-dark-muted dark:text-neutral-400">
                {t("pages.conquest.features.noHassle.description")}
              </p>
            </motion.div>
          </div>
        </div>

        {/* How it works */}
        <SectionBackground className="text-center py-16 justify-center items-center">
          <Chip
            title={t("howItWorks.chipTitle")}
            variant="primary"
            icon={<Footprints strokeWidth={2} />}
          />
          <HowItWorks compact />
        </SectionBackground>

        {/* Interactive Question */}
        <div className="px-4 sm:px-6 py-16 bg-white dark:bg-neutral-900">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-type-950 dark:text-neutral-50">
              {t("quiz.question")}
            </h2>
            <div className="flex flex-row gap-4 justify-center items-stretch">
              <a
                href={QUIZ_HREF}
                className="flex-1 sm:flex-none"
                onClick={() => tracking.landingHeadFirst()}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="card max-w-40 max-h-40 w-full h-full hover:shadow-xl hover:border-primary-500 transition-all cursor-pointer group"
                >
                  <div className="text-5xl mb-3">🧠</div>
                  <div className="text-xl font-semibold text-type-950 dark:text-neutral-100 group-hover:text-primary-500 transition-colors">
                    {t("quiz.headFirst")}
                  </div>
                </motion.button>
              </a>
              <a
                href={QUIZ_HREF}
                className="flex-1 sm:flex-none"
                onClick={() => tracking.landingHeartFirst()}
              >
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="card max-w-40 max-h-40 w-full h-full hover:shadow-xl hover:border-primary-500 transition-all cursor-pointer group"
                >
                  <div className="text-5xl mb-3">❤️</div>
                  <div className="text-xl font-semibold text-type-950 dark:text-neutral-100 group-hover:text-primary-500 transition-colors">
                    {t("quiz.heartFirst")}
                  </div>
                </motion.button>
              </a>
            </div>
            <a href={QUIZ_HREF} className="btn-primary inline-block p-4 px-8 mt-6">
              {t("quiz.cta")}
            </a>
          </motion.div>
        </div>

        {/* Press */}
        <div className="bg-white dark:bg-neutral-900 text-center px-4 sm:px-6 py-16 lg:px-8 justify-center items-center">
          <Chip
            title={t("press.chipTitle")}
            variant="primary"
            icon={<Newspaper strokeWidth={2} />}
          />
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mt-8">
            <a
              href="https://www.timeout.com/newyork/news/this-new-site-sets-lgbtq-new-yorkers-up-on-friend-datesso-i-sat-down-for-dinner-with-strangers-111225"
              target="_blank"
              rel="noopener noreferrer"
              className="card p-6 text-left flex flex-col hover:border-primary-500/50 transition-colors"
            >
              <img src={timeoutImg} alt="Timeout" className="dark:invert mb-4 h-8 w-auto self-start" />
              <p className="text-dark-muted dark:text-neutral-400 italic flex-1">
                {t("pages.conquest.press.timeoutQuote")}
              </p>
            </a>
            <a
              href="https://www.them.us/story/outclose-gayborhood-app-queer-dinners-finding-friends"
              target="_blank"
              rel="noopener noreferrer"
              className="card p-6 text-left flex flex-col hover:border-primary-500/50 transition-colors"
            >
              <img src={themImg} alt="Them" className="dark:invert mb-4 h-8 w-auto self-start" />
              <p className="text-dark-muted dark:text-neutral-400 italic flex-1">
                {t("pages.conquest.press.themQuote")}
              </p>
            </a>
          </div>
        </div>

        {/* Testimonials */}
        <SectionBackground className="text-center px-4 sm:px-6 py-16 lg:px-8 justify-center items-center">
          <Chip
            title={t("testimonials.chipTitle")}
            variant="primary"
            icon={<MessageCircle strokeWidth={2} />}
          />
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mt-8">
            {([1, 2, 3, 4] as const).map((n, i) => (
              <motion.div
                key={n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * (i + 1), duration: 0.5 }}
                className="card p-6 text-left flex flex-col"
              >
                <p className="text-dark-muted dark:text-neutral-400 mb-4 italic flex-1">
                  {t(`pages.conquest.testimonials.quote${n}`)}
                </p>
                <p className="font-semibold text-type-950 dark:text-neutral-100 flex items-center gap-2">
                  {t(`pages.conquest.testimonials.author${n}`)}
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-500">
                    <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {t("testimonials.verifiedMember")}
                  </span>
                </p>
              </motion.div>
            ))}
          </div>
        </SectionBackground>

        {/* Meet Your Future Table */}
        <div className="py-8 bg-white dark:bg-neutral-900">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-5xl mx-auto"
          >
            <div className="px-4">
              <h2 className="text-3xl md:text-4xl font-semibold mb-2 text-type-950 dark:text-neutral-50">
                {t("meetTable.title")}
              </h2>
              <p className="text-lg text-dark-muted dark:text-neutral-400 mb-4">
                {t("meetTable.newTablesEveryWeek")}
              </p>
            </div>

            <div className="overflow-x-scroll scrollbar-hidden w-full max-w-7xl mx-auto">
              <div className="flex flex-row gap-8 p-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img src={danielImg} alt="Marcus" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      MARCUS, 29 ♌
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      {t("pages.men2040.people.marcusBio")}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: 2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img src={jayImg} alt="Tyler" className="w-full h-full object-cover object-[center_20%]" />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      TYLER, 35 ♏
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      {t("pages.men2040.people.tylerBio")}
                    </p>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, rotate: -2 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="card p-0 overflow-hidden min-w-64"
                >
                  <div className="w-full h-64 overflow-hidden">
                    <img src={davidImg} alt="David" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-6 text-left">
                    <h3 className="text-2xl font-semibold text-type-950 dark:text-neutral-100 mb-2">
                      DAVID, 55 ♉
                    </h3>
                    <p className="text-dark-muted dark:text-neutral-400">
                      {t("pages.men40.people.davidBio")}
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* FAQ */}
        <SectionBackground className="text-center px-4 sm:px-6 pt-16 lg:px-8 pb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-8 text-type-950 dark:text-neutral-50 font-Poppins">
            {t("faq.chipTitle")}
          </h2>
          <div className="flex flex-col gap-4 max-w-xl mx-auto">
            <Expandable title={t("faq.whatsIncluded.question")}>
              {t("faq.whatsIncluded.answer")}
            </Expandable>
            <Expandable title={t("faq.howAssigned.question")}>
              {t("faq.howAssigned.answer")}
            </Expandable>
            <Expandable title={t("faq.whoCanJoin.question")}>
              {t("pages.conquest.faq.whoCanJoinAnswer")}
            </Expandable>
            <Expandable title={t("faq.isDating.question")}>
              {t("faq.isDating.answer")}
            </Expandable>
            <Expandable title={t("faq.bringPartner.question")}>
              {t("faq.bringPartner.answer")}
            </Expandable>
            <Expandable title={t("faq.cost.question")}>
              {t("faq.cost.answer")}
            </Expandable>
            <Expandable title={t("faq.whoBehind.question")}>
              {t("pages.conquest.faq.whoBehindAnswer")}
            </Expandable>
          </div>
        </SectionBackground>

        {/* Footer */}
        <div className="bg-white dark:bg-neutral-900 pb-16 md:mb-0">
          <div className="max-w-7xl mx-auto px-4 py-8 text-center">
            <p className="mb-8">
              <a href={QUIZ_HREF} className="btn-primary mx-auto px-8 py-4 w-auto">
                {t("footer.signUpNow")}
              </a>
            </p>
            <AppStoreBadges className="justify-center mb-6" badgeHeight={36} source="landing_conquest_footer" />
            <img src={logo} alt="Logo" className="w-16 h-16 inline-block dark:invert" />
            <p className="text-2xl font-Poppins font-bold mb-2 dark:text-neutral-100">Outclose</p>
            <div className="flex justify-center items-center gap-2 mb-2">
              <a href="https://instagram.com/outclose.lgbt" target="_blank">
                <img
                  src={imgInstagram}
                  alt="Instagram"
                  className="inline-block ml-2 w-10 h-10 p-2 text-primary-500 dark:invert"
                />
              </a>
              <a href="https://www.tiktok.com/@outclose.lgbt" target="_blank">
                <img
                  src={imgTiktok}
                  alt="TikTok"
                  className="inline-block ml-2 w-10 h-10 p-2 text-primary-500 dark:invert"
                />
              </a>
            </div>
            <span className="dark:text-neutral-300">{t("footer.tagline")}</span>

            <div className="my-4 flex justify-center">
              <LanguageSelector className="dark:text-neutral-100 dark:border-neutral-600" />
            </div>
            <p className="my-4 text-sm text-center">
              <a
                href="#"
                className="cky-banner-element hover:text-primary-500"
                onClick={() => (window as any).revisitCkyConsent?.()}
              >
                {t("footer.manageCookies")}
              </a>
            </p>
            <p className="my-4 text-sm">
              <Link href="/terms" className="hover:text-primary-500 transition-colors">
                {t("footer.terms")}
              </Link>
              {" | "}
              <Link href="/privacy" className="hover:text-primary-500 transition-colors">
                {t("footer.privacy")}
              </Link>
              {" | "}
              <Link href="/community-guidelines" className="hover:text-primary-500 transition-colors">
                {t("footer.communityGuidelines")}
              </Link>
            </p>
            <p className="my-4 text-sm flex items-center justify-center gap-1.5 dark:text-neutral-400">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
              {t("footer.proudlyGayOwned")}
            </p>
            <p className="my-4 text-sm dark:text-neutral-400">
              <a href="https://outclose.com/company" className="hover:text-primary-500 transition-colors">
                GBH Holdings Inc.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LandingConquest;
